import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Info} from "@phosphor-icons/react";
import { awardSchema, defaultAward } from "@reactive-resume/schema";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@reactive-resume/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { SectionDialog } from "../sections/shared/section-dialog";
import { YearSelect } from "../sections/shared/year-select";
import { URLInput } from "../sections/shared/url-input";
import { MastersUnionListEditor } from "../sections/masters-union-summary";

const formSchema = awardSchema;

type FormValues = z.infer<typeof formSchema>;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const SingleMonthYearPicker = ({
  value,
  onChange,
}: {
  value?: string;
  onChange: (value: string) => void;
}) => {
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  useEffect(() => {
    if (!value) {
      setMonth("");
      setYear("");
      return;
    }

    const parts = value.trim().split(" ");
    if (parts.length === 2) {
      const monthIndex = MONTHS_SHORT.findIndex(
        (item) => item.toLowerCase() === parts[0].toLowerCase(),
      );

      if (monthIndex !== -1) {
        setMonth(MONTHS[monthIndex]);
        setYear(parts[1]);
        return;
      }
    }

    setMonth("");
    setYear(value.trim());
  }, [value]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 51 }, (_, i) => String(currentYear - i));

  const formatValue = (nextMonth: string, nextYear: string) => {
    if (!nextYear) return "";
    if (!nextMonth) return nextYear;

    const monthIndex = MONTHS.indexOf(nextMonth as (typeof MONTHS)[number]);
    if (monthIndex === -1) return nextYear;

    return `${MONTHS_SHORT[monthIndex]} ${nextYear}`;
  };

  return (
    <div className="grid gap-4" style={{gridTemplateColumns: '1fr 1fr'}}>
      <Select
        value={month}
        onValueChange={(nextMonth) => {
          setMonth(nextMonth);
          onChange(formatValue(nextMonth, year));
        }}
      >
        <SelectTrigger className="flex-1 min-w-[180px]">
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent className="[&>div]:max-h-[300px] [&>div]:overflow-auto">
          {MONTHS.map((item) => (
            <SelectItem key={item} value={item}>
              {item}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <YearSelect
        value={year}
        onValueChange={(nextYear) => {
          setYear(nextYear);
          onChange(formatValue(month, nextYear));
        }}
        options={years}
        placeholder="Year"
        searchPlaceholder="Search year"
        className="flex-1 min-w-[180px]"
      />
    </div>
  );
};

export const AwardsDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultAward,
    resolver: zodResolver(formSchema),
  });

  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const isMastersUnion = template === "mastersunion";
  const { isOpen } = useDialog<FormValues>("awards");

  useEffect(() => {
    if (!isOpen || !isMastersUnion) return;

    form.setValue("title", "Achievement", { shouldValidate: true });
    form.setValue("awarder", "", { shouldValidate: false });
    form.setValue("url", defaultAward.url, { shouldValidate: false });
  }, [form, isMastersUnion, isOpen]);

  return (
    <SectionDialog<FormValues> id="awards" form={form} defaultValues={defaultAward}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {!isMastersUnion && (
          <>
            <FormField
              name="title"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t({ message: "Title", context: "Name of the Award" })}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Award Title" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="awarder"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Awarder`}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Awarder Name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <FormField
          name="date"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Date`}</FormLabel>
              <FormControl>
                {isMastersUnion ? (
                  <SingleMonthYearPicker value={field.value} onChange={field.onChange} />
                ) : (
                  <Input
                    {...field}
                    placeholder={t({
                      message: "March 2023",
                      comment: "The month and year should be uniform across all languages.",
                    })}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isMastersUnion && (
          <FormField
            name="url"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Website`}</FormLabel>
                <FormControl>
                  <URLInput {...field} placeholder="https://www.example.com" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              {/* <FormLabel>{t`Summary`}</FormLabel> */}
              <FormLabel className="flex items-center gap-2">
                <span>{t`Summary`}</span>
                {isMastersUnion && (
                  <Tooltip content="Click on “Add More” to add more bullet points">
                    <span className="inline-flex cursor-help text-muted-foreground">
                      <Info size={14} />
                    </span>
                  </Tooltip>
                )}
              </FormLabel>
              <FormControl>
                {isMastersUnion ? (
                  <MastersUnionListEditor
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                ) : (
                  <RichInput
                    {...field}
                    content={field.value}
                    onChange={(value) => field.onChange(value)}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        content={editor.getHTML()}
                        onChange={(value) => {
                          editor.commands.setContent(value);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
